/* Queue.tsx — the work queue. The rep's day, and the only screen in the product that WRITES.
 *
 * Ownerless by instruction (0 reps assigned). This used to be one rep's book; it
 * is now the whole book, ranked. The ranking, the reason-in-the-row and the correction keys are the
 * parts that were ever worth anything here — the owner slice was a placeholder.
 *
 * Two defects this port keeps fixed, both of the same shape: the endpoint has ALWAYS returned `acts`
 * and `tags`, and nothing read them. A rep's logged call looked like it worked and the same name was
 * back at the top of the queue next session with no marker on it; a tag set yesterday was invisible
 * today so you set it again. Both are seeded from the SERVER below, not from an empty client object.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, isErr, post } from '../lib/api.ts';
import { N, SQ, LVLNAME, ROLE_SHORT, ROLE_COLOR, phoneFmt, safeUrl, distText } from '../lib/fmt.ts';
import { Bar, Btn, Chip, Copy, ErrBox, Hint, Section, Seg, Stat, HAIR, TierTag } from '../components/ui.tsx';
import { PeopleList } from '../components/data.tsx';

interface QRow {
  person_id: number; full_name: string; title: string; dept: string | null; level: number;
  icp_role: string; icp_conf: number | null; email: string | null; phone: string | null;
  switchboard: number | null; city: string | null; state: string | null;
  facility_site: string | null; facility_city: string | null; facility_state: string | null;
  facility_dist_mi: number | null; facility_conf: string | null; facility_id: number | null;
  linkedin_url: string | null; domain: string; company: string; tier: string | null;
  domain_mismatch: number | null; location_source: string | null;
  plant_sqft: number | null; plant_sqft_conf: string | null; plant_committee: number | null;
  plant_reach: number | null; acct_plants: number | null; acct_reach: number | null;
}
interface QueueRes {
  rows: QRow[];
  book: Record<string, number>;
  tags: { key: string; tag: string }[];
  acts: { key: string; kind: string }[];
}

const QTAGS = ['champion', 'gatekeeper', 'has-robots', 'hand-sanding', 'budget-q3', 'referred', 'do-not-contact'];

const LOG: [key: string, label: string, kind: string][] = [
  ['c', 'Called', 'call'], ['e', 'Emailed', 'email'], ['m', 'Left voicemail', 'vm'],
  ['b', 'Booked a meeting', 'meeting'], ['s', 'Skip', 'skip'],
];
const FIX: [key: string, label: string, kind: string][] = [
  ['x', 'Wrong person', 'bad_person'], ['g', 'Gone / left', 'gone'],
  ['p', 'Wrong plant', 'bad_plant'], ['d', 'This is the real DM', 'is_dm'],
];
/* A correction is not a note in a file — it says where the fix lands. Naming the destination is what
   makes the button worth pressing twice. */
const NOTE: Record<string, string> = {
  bad_person: 'routed to the correction queue: the title classifier sees this as a mislabel',
  gone: 'routed to the refresh queue: the record is flagged stale for re-enrichment',
  bad_plant: 'routed to placement: the ≤100mi assignment for this person is disputed',
  is_dm: 'routed to scoring: a rep-confirmed decision maker outranks the model',
};

export function Queue({ onOpenPerson, onOpenPlant }: {
  onOpenPerson?: (id: number) => void;
  onOpenPlant?: (facId: number) => void;
}) {
  const [mode, setMode] = useState<'best' | 'plant' | 'exec'>('best');
  const [d, setD] = useState<QueueRes | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [i, setI] = useState(0);
  const [done, setDone] = useState<Record<string, string>>({});
  const [ptags, setPtags] = useState<Record<string, string[]>>({});
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    let dead = false;
    setD(null); setErr(null);
    api<QueueRes>('queue?mode=' + mode).then((r) => {
      if (dead) return;
      if (isErr(r)) { setErr(r.error); return; }
      setD(r); setI(0);
      const dn: Record<string, string> = {};
      for (const a of r.acts || []) if (!dn[a.key]) dn[a.key] = a.kind;   // acts come back newest first
      setDone(dn);
      const tg: Record<string, string[]> = {};
      for (const t of r.tags || []) (tg[t.key] ||= []).push(t.tag);
      setPtags(tg);
    });
    return () => { dead = true; };
  }, [mode]);

  const rows = d?.rows ?? [];
  const r = rows[i];

  /* Optimistic, and explicit about it: the row flips immediately and flips BACK, with a visible
     message, if the write did not land. A call that looks logged and is not was the original sin of
     this queue — the seed-from-server fix covered reads, this covers the write path. */
  const logAct = useCallback(async (row: QRow, kind: string) => {
    const prev = done[row.person_id];
    setDone((p) => ({ ...p, [row.person_id]: kind }));
    const res = await post<{ ok: boolean }>('mutate?' + new URLSearchParams({ e: 'ppl', key: String(row.person_id), kind, by: 'prototype' }));
    if (isErr(res)) {
      setDone((p) => {
        const n = { ...p };
        if (prev === undefined) delete n[String(row.person_id)]; else n[String(row.person_id)] = prev;
        return n;
      });
      setToast(`Not saved — retry. ${res.error}`);
      return;
    }
    setToast(NOTE[kind] ? `Logged "${kind}" — ${NOTE[kind]}.` : `Logged "${kind}" against ${row.full_name}.`);
    setI((x) => Math.min(rows.length - 1, x + 1));
  }, [done, rows.length]);

  /* A toggle, and the state is ON the button. Before this, clicking a tag wrote it and printed the
     result into a separate span, so the button never changed, there was no way to take one off, and
     a tag set yesterday did not show at all. */
  const toggleTag = useCallback(async (row: QRow, tag: string) => {
    const key = String(row.person_id);
    const cur = ptags[key] || [];
    const on = cur.includes(tag);
    setPtags((p) => ({ ...p, [key]: on ? cur.filter((t) => t !== tag) : [...cur, tag] }));
    const res = await post('mutate?' + new URLSearchParams({ e: 'ppl', key, tag, op: on ? 'del' : 'add', by: 'prototype' }));
    if (isErr(res)) {
      setPtags((p) => ({ ...p, [key]: cur }));       // roll back to what the server holds
      setToast(`Tag not saved — retry. ${res.error}`);
    }
  }, [ptags]);

  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(null), 4200);
    return () => clearTimeout(id);
  }, [toast]);

  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || /input|select|textarea/i.test((e.target as Element)?.tagName || '')) return;
      if (!r) return;
      const K: Record<string, string> = Object.fromEntries([...LOG, ...FIX].map(([k, , kind]) => [k, kind]));
      if (e.key === 'j') setI((x) => Math.min(rows.length - 1, x + 1));
      else if (e.key === 'k') setI((x) => Math.max(0, x - 1));
      else if (K[e.key]) logAct(r, K[e.key]);
    };
    addEventListener('keydown', on);
    return () => removeEventListener('keydown', on);
  }, [r, rows.length, logAct]);

  const worked = useMemo(() => rows.filter((x) => done[x.person_id]).length, [rows, done]);

  if (err) return <ErrBox what="The queue did not load" msg={err} />;
  if (!d) return <Hint style={{ padding: 'var(--gut)' }}>loading the queue…</Hint>;

  return (
    <div style={{ display: 'flex', height: '100%', minHeight: 0, position: 'relative' }}>
      <aside style={{ width: 320, flex: '0 0 320px', borderRight: HAIR, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Section label="Work queue">
          <Seg wide value={mode} onPick={setMode}
            options={[['best', 'Best next'], ['plant', 'Biggest plant'], ['exec', 'Most senior']]} />
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 10 }}>
            <span className="num" style={{ color: 'var(--am)', fontSize: 'var(--t-fig)' }}>{worked}</span>
            <span className="t-label">of {rows.length} worked</span>
          </div>
          <Bar frac={rows.length ? worked / rows.length : 0} style={{ marginTop: 5 }} />
          <Hint style={{ marginTop: 7 }}>Nothing is assigned to anyone yet. This is the whole book, ranked.</Hint>
        </Section>
        <div style={{ flex: '1 1 auto', overflowY: 'auto', minHeight: 0 }}>
          {rows.map((row, ix) => (
            <div key={row.person_id} onClick={() => setI(ix)}
              style={{
                padding: '7px var(--gut)', borderBottom: HAIR, cursor: 'pointer',
                background: ix === i ? 'rgba(255,170,0,.043)' : undefined,
                // A border, not a box-shadow — the Atlas reset strips shadows. See ui.tsx Btn.
                borderLeft: ix === i ? '2px solid var(--or)' : '2px solid transparent',
                opacity: done[row.person_id] ? 0.55 : 1,
              }}>
              <div style={{ display: 'flex', gap: 6, alignItems: 'baseline' }}>
                <span className="num t-micro" style={{ color: 'var(--dim2)', width: 22 }}>{ix + 1}</span>
                <span style={{ fontWeight: 600 }}>{row.full_name}</span>
              </div>
              <Hint style={{ paddingLeft: 28 }}>{row.title}</Hint>
              <Hint style={{ paddingLeft: 28, color: 'var(--dim2)' }}>
                {row.company} · {row.facility_city || row.city || ''}{row.facility_state ? ', ' + row.facility_state : ''}
              </Hint>
              <div style={{ display: 'flex', gap: 5, alignItems: 'center', paddingLeft: 28, marginTop: 3 }}>
                <span style={{ color: ROLE_COLOR[row.icp_role] || 'var(--dim2)', fontSize: 'var(--t-micro)' }}>{ROLE_SHORT[row.icp_role]}</span>
                <TierTag tier={row.tier} />
                <span title={`${row.phone ? 'phone' : 'no phone'} · ${row.email ? 'email' : 'no email'} on file`}
                  className="t-micro" style={{ color: 'var(--dim2)' }}>
                  <span style={{ color: row.phone ? 'var(--ok)' : 'var(--dim2)' }}>☎</span>
                  <span style={{ color: row.email ? 'var(--bl)' : 'var(--dim2)' }}>✉</span>
                </span>
                {done[row.person_id] && <Chip tone="warn">{done[row.person_id]}</Chip>}
              </div>
            </div>
          ))}
        </div>
      </aside>

      <section style={{ flex: '1 1 auto', minWidth: 0, overflowY: 'auto' }}>
        {!r ? <Hint style={{ padding: 'var(--gut)' }}>queue empty</Hint>
          : <Dossier r={r} tags={ptags[String(r.person_id)] || []}
            onLog={(k) => logAct(r, k)} onTag={(t) => toggleTag(r, t)}
            onOpenPerson={onOpenPerson} onOpenPlant={onOpenPlant} />}
      </section>

      {toast && (
        <div style={{
          position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 5,
          background: 'var(--s3)', border: HAIR, padding: '8px 12px', maxWidth: 620,
          fontSize: 'var(--t-body)', color: 'var(--ink)',
        }}>{toast}</div>
      )}
    </div>
  );
}

/* Deliberately conservative: the opener states ONLY what the dataset holds. The moment a draft
   asserts a process detail we never collected, the rep stops trusting every other line in it. */
function opener(r: QRow) {
  const site = r.facility_site || (r.facility_city ? `your ${r.facility_city} site` : 'your plant');
  const sq = r.plant_sqft ? `${SQ(r.plant_sqft)} sq ft` : null;
  const who = r.level >= 6 ? 'you' : 'your team';
  return `"${(r.full_name || '').split(' ')[0]} — you run ${r.dept === 'Quality' ? 'quality' : r.dept === 'Operations' ? 'operations' : 'manufacturing'} at ${site}`
    + `${sq ? `, and it is one of the larger sites we see at ${r.company} at ${sq}` : ''}. `
    + 'We put robot cells on finishing work — sanding, polishing, deburring — in high-mix plants, and the reason I am calling '
    + `${who} rather than corporate is that the decision usually gets made in the building. `
    + 'Is finishing a bottleneck on your line right now, or is it somewhere else?"';
}

function Dossier({ r, tags, onLog, onTag, onOpenPerson, onOpenPlant }: {
  r: QRow; tags: string[]; onLog: (kind: string) => void; onTag: (tag: string) => void;
  onOpenPerson?: (id: number) => void; onOpenPlant?: (facId: number) => void;
}) {
  const lu = safeUrl(r.linkedin_url);
  const dist = r.facility_conf === 'geo-25' ? distText(r.facility_dist_mi, r.location_source) : null;

  /* WHY THIS NAME, as facts rather than a sentence. It used to be a three-line paragraph with the
     geocode caveat spliced into the middle — the longest block of text on a screen whose whole job
     is a phone call. The facts are unchanged and the caveats are on hover. */
  const sqftBad = r.plant_sqft_conf && r.plant_sqft_conf !== 'ok';

  /* Only the things that CHANGE WHAT THE REP DOES before they pick up. */
  const warn: string[] = [];
  if (!r.email && r.phone) warn.push('Phone only. The provider held no address for them and this app never constructs one, so do not put them in an email sequence.');
  if (!r.phone && r.email) warn.push('No phone on file. Email or LinkedIn only.');
  if (!r.phone && !r.email) warn.push('Neither a phone nor an email. LinkedIn is the only route we hold.');
  if (r.switchboard) warn.push('That is a switchboard, not a direct line — expect to be routed.');
  if (r.domain_mismatch) warn.push('Mail domain is not the employer domain. Check it before you send.');
  if (!r.facility_id) warn.push('Not placed at a plant. There is no building to work as a group, so this is a single call.');

  return (
    <div>
      <div style={{ padding: 'var(--gut)', borderBottom: HAIR }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600, letterSpacing: '-.02em' }}>{r.full_name}</h1>
          <span style={{ color: ROLE_COLOR[r.icp_role] || 'var(--dim2)', fontSize: 'var(--t-micro)' }}>{ROLE_SHORT[r.icp_role]}</span>
          <TierTag tier={r.tier} />
        </div>
        <div style={{ color: 'var(--dim)', marginTop: 3 }}>{r.title}</div>
        <Hint style={{ marginTop: 4 }}>
          {r.company} · {r.facility_city || r.city || ''}{r.facility_state ? ', ' + r.facility_state : ''}
          {lu && <> · <a href={lu} target="_blank" rel="noopener" style={{ color: 'var(--bl)' }}>LinkedIn ↗</a></>}
        </Hint>
      </div>

      {/* The address and the number ARE the deliverable of this system, so they lead. */}
      <div style={{ display: 'flex', borderBottom: HAIR }}>
        <div style={{ flex: 1, padding: 'var(--gut)', borderRight: HAIR }}>
          <div className="t-label">Call</div>
          {r.phone ? (
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 4 }}>
              <Copy value={r.phone} label={phoneFmt(r.phone)} style={{ fontSize: 'var(--t-lead)' }} />
              <a href={`tel:${r.phone}`} className="t-micro" style={{ color: 'var(--or)' }}>dial ↗</a>
            </div>
          ) : <Hint style={{ marginTop: 4 }}>no phone on file</Hint>}
        </div>
        <div style={{ flex: 1, padding: 'var(--gut)' }}>
          <div className="t-label">Email</div>
          {r.email ? (
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 4 }}>
              <Copy value={r.email} style={{ fontSize: 'var(--t-body)' }} />
              <a href={`mailto:${r.email}`} className="t-micro" style={{ color: 'var(--or)' }}>write ↗</a>
            </div>
          ) : <Hint style={{ marginTop: 4 }}>no address — the provider held none and nothing is ever constructed</Hint>}
        </div>
      </div>

      {warn.length > 0 && (
        <div style={{ padding: 'var(--gut)', borderBottom: HAIR, borderLeft: '2px solid var(--rd)' }}>
          {warn.map((w) => <div key={w} style={{ color: 'var(--dim)', lineHeight: 1.6 }}>{w}</div>)}
        </div>
      )}

      <Section label="Why this name">
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
          {r.plant_sqft != null && (
            <Chip tone={sqftBad ? 'soft' : 'none'}
              title={sqftBad ? 'This figure is not trusted as floor area, so it was excluded from the ranking. It still displays exactly as sent.' : undefined}>
              {SQ(r.plant_sqft)} sq ft plant{sqftBad ? '*' : ''}
            </Chip>
          )}
          {dist && <Chip tone={dist.soft ? 'soft' : 'none'} title={dist.why}>{dist.text}</Chip>}
          {r.plant_committee === 4
            ? <Chip tone="ok">all 4 buying roles here</Chip>
            : r.plant_committee != null && <Chip>{r.plant_committee} of 4 roles here</Chip>}
          <Chip>level {r.level} · {LVLNAME[r.level] || ''}</Chip>
          <Chip tone={(r.icp_conf ?? 0) >= 70 ? 'info' : 'bad'}
            title="How sure the title classifier is that this title carries this buying role.">
            title read {r.icp_conf ?? '?'}%
          </Chip>
        </div>
      </Section>

      <Section label="Log it" right={<Hint><Kbd>j</Kbd> <Kbd>k</Kbd> move through the queue</Hint>}>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {LOG.map(([k, label, kind]) => (
            <Btn key={kind} onClick={() => onLog(kind)}><Kbd>{k}</Kbd> {label}</Btn>
          ))}
        </div>
        <Hint style={{ margin: '10px 0 5px' }}>Wrong? Fix it once, for everyone with that title or at that plant:</Hint>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {FIX.map(([k, label, kind]) => (
            <Btn key={kind} sm onClick={() => onLog(kind)}><Kbd>{k}</Kbd> {label}</Btn>
          ))}
        </div>
      </Section>

      <Section label="Opening line" right={<Hint>every clause traces to a field on this screen</Hint>}>
        <div style={{ border: HAIR, padding: 'var(--gut)', fontSize: 13, lineHeight: 1.65, color: 'var(--ink)' }}>
          {opener(r)}
        </div>
      </Section>

      <Section label="The rest of the committee in this building — call them in one pass, not one a week">
        {r.facility_id
          ? <PeopleList params={{ fac: r.facility_id }} omit={r.person_id} onPick={onOpenPerson} cap={40} />
          : <Hint>Not placed at a plant, so there is no building to work as a group. Placement is the gap to close first.</Hint>}
      </Section>

      <div style={{ display: 'flex', borderBottom: HAIR, borderTop: HAIR }}>
        <div style={{ flex: 1, padding: 'var(--gut)', borderRight: HAIR }}>
          <div className="t-label">The building</div>
          <div style={{ fontWeight: 600, marginTop: 5 }}>{r.facility_site || 'not placed at a plant'}</div>
          <Hint>{r.facility_city || ''}{r.facility_state ? ', ' + r.facility_state : ''}</Hint>
          <div style={{ display: 'flex', marginTop: 8 }}>
            <Stat label="Sq ft" value={SQ(r.plant_sqft)} />
            <Stat label="Reachable here" value={N(r.plant_reach)} />
            <Stat label="Committee" value={`${r.plant_committee ?? 0}/4`} />
          </div>
          <Btn style={{ marginTop: 10 }} disabled={!r.facility_id}
            title={r.facility_id ? undefined : 'Not placed at a plant, so there is no building to open on the map.'}
            onClick={() => r.facility_id && onOpenPlant?.(r.facility_id)}>Open in Atlas →</Btn>
        </div>
        <div style={{ flex: 1, padding: 'var(--gut)' }}>
          <div className="t-label">The account</div>
          <div style={{ fontWeight: 600, marginTop: 5 }}>{r.company}</div>
          <Hint>{r.domain}</Hint>
          <div style={{ display: 'flex', marginTop: 8 }}>
            <Stat label="Plants" value={N(r.acct_plants)} />
            <Stat label="Reachable" value={N(r.acct_reach)} />
          </div>
        </div>
      </div>

      <Section label="Tags">
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {QTAGS.map((t) => (
            <Btn key={t} sm on={tags.includes(t)} onClick={() => onTag(t)}>{tags.includes(t) ? '✓' : '+'} {t}</Btn>
          ))}
        </div>
      </Section>
    </div>
  );
}

const Kbd = ({ children }: { children: React.ReactNode }) => (
  <span style={{
    fontFamily: 'var(--mono)', fontSize: 10, border: HAIR, padding: '0 3px',
    color: 'var(--dim2)', marginRight: 3,
  }}>{children}</span>
);
